import { useContext, useState } from 'react';
import * as htmlToImage from 'html-to-image';
import { waitForElement } from '../utils/domUtils';
import { AuthContext } from '../context/AuthContext';
// import useIndexedDB from './useIndexedDB';

const useImageHandling = () => {
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    // const { db, storeImages } = useIndexedDB();
    const { user, indexedDB, addImagesToDB, addWishlistImagesToDB } = useContext(AuthContext);

    // Helper function to retry canvas generation until a better result is found.
    const createCanvasWithRetry = async (node) => {
        const isSafariOrChrome =
        /safari|chrome/i.test(navigator.userAgent) && !/android/i.test(navigator.userAgent);
        let dataUrl = "";
        let canvas;
        let attempt = 0;
        const maxAttempts = isSafariOrChrome ? 5 : 1;
        const cycle = [];
        let repeat = true;
    
        while (repeat && attempt < maxAttempts) {
        canvas = await htmlToImage.toCanvas(node, {
            fetchRequestInit: { cache: "no-cache" },
            includeQueryParams: true,
            quality: 1,
        });
        attempt += 1;
        dataUrl = canvas.toDataURL("image/png");
        cycle[attempt] = dataUrl.length;
        // On first attempt, we have nothing to compare against.
        if (attempt > 1) {
            // If the current dataUrl is longer than the previous one,
            // assume it's a more complete render.
            if (attempt >= 3 && dataUrl.length === cycle[attempt - 1]) {
            repeat = false;
            }
        }
        // Optional: a short delay between attempts
        // await new Promise((resolve) => setTimeout(resolve, 200));
        }
        console.log("is safari or chrome: " + isSafariOrChrome + " _repeat_need_ " + attempt);
        return dataUrl;
    };

    const sendHtmlToServer = async () => {
        try {
            const startTime = performance.now();
            setIsLoading(true);

            const container = await waitForElement('#downloadImageTemplate');
            const fileUrls = [];

            const nodes = container.querySelectorAll('.image-template');
            if(nodes.length === 0) throw new Error("No nodes found inside the nodes container");
            
            const processingStart = performance.now();
            console.log(`Finding nodes took: ${processingStart - startTime}ms`);

            // process each node using retry logic, to get a reliable png data URL
            for(let i = 0; i < nodes.length; i++){
                let node = nodes[i];
                const dataUrl = await createCanvasWithRetry(node);
                console.log("pushing dataUrl into cache...");
                // await new Promise(resolve => setTimeout(resolve, 400));
                fileUrls.push(dataUrl);
                // console.log("dataUrl", dataUrl);
            }
            
            console.log(`Image processing took: ${performance.now() - processingStart}ms`);
            await cacheImageUrls(fileUrls, user.username);

            console.log(`Total time: ${performance.now() - startTime}ms`);
            setIsLoading(false);
            return true;

        } catch (error) {
            console.error("Could not save images in wishlist. Please try again later.", error);
            return false;
        }
    };
    
    const cacheImageUrls = async (urls, username) => {
        try {
            if(indexedDB){
                addImagesToDB(urls, username);
            } else {
                console.warn("Indexed DB not ready for caching");
            }
        } catch (error) {
            console.error("Could not save images locally.", error);
            setError("Could not save images locally. Wishlist images may not save properly.");
        }
    }

    const processImages = async (details) => {
        let imgUrls = [];
        if(details.type === 'wishlist'){
            const wishlistItems = details.wishlistItems;
            for(let item of wishlistItems){
                try {
                   // Fetch the image from the URL and convert it to a base64 string
                    const response = await fetch(item.imageUrl);
                    const blob = await response.blob();
                    
                    // Convert the blob to a data URL (base64 encoding)
                    const reader = new FileReader();
                    await new Promise(resolve => {
                        reader.onloadend = () => {
                            resolve();
                        };
                        reader.onerror = (error) => {
                            console.error("Error converting image to base64:", error);
                            resolve(); // Resolve to continue even if there was an error
                        };
                        reader.readAsDataURL(blob);
                    });

                    imgUrls.push({ itemName: item.name, url: reader.result });
                } catch (error) {
                    console.error("Error processing image for item:", item.id, error);
                    return false;
                }
            }
            if(imgUrls.length > 0){
                await addWishlistImagesToDB(imgUrls, user.username);
                return true;
            }
        } else if(details.type === 'item'){
            console.log("details: ", details);
            const linkItemImage = details.photo;
            try {
                const response = await fetch(linkItemImage);
                const blob = await response.blob();

                // convert blob into base64 encoding
                const reader = new FileReader();
                await new Promise(resolve => {
                    reader.onloadend = () => {
                        resolve();
                    };
                    reader.onerror = (error) => {
                        console.error("Error converting image to base64", error);
                        resolve();
                    };
                    reader.readAsDataURL(blob);
                });
                imgUrls.push({ itemName: details.title, url: reader.result });
            } catch (error) {
                console.error("Error processing image for item: ", error);
                return false
            }
        }
        if(imgUrls.length > 0){
            await addWishlistImagesToDB(imgUrls, user.username);
            return true;
        }
    };

    return {
        sendHtmlToServer,
        processImages,
        cacheImageUrls,
        error,
        isLoading,
    };
};

export default useImageHandling;